import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';
import { DisambiguationSectionComponent } from './section/disambiguation-section.component';
import { FeatureDetail } from '../../typings/features/feature-detail';

/**
 * A dialog component used to handle disambiguation of multiple features.
 * This component displays a list of candidates grouped by their navigation type
 * and allows the user to select one of them.
 *
 * To replace this component with your own:
 * - Ensure your component implements a similar interface for receiving `DIALOG_DATA` and returning a selection.
 * - Update the `DisambiguationDialogService` to reference your custom component.
 */
@Component({
  selector: 'jmkw-disambiguation-dialog',
  standalone: true,
  imports: [CommonModule, DisambiguationSectionComponent],
  templateUrl: './disambiguation-dialog.component.html',
  styleUrl: './disambiguation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisambiguationDialogComponent {
  // Logger instance for debugging and tracing.
  private readonly _logger = inject(NGXLogger);

  // ChangeDetectorRef for manually triggering change detection.
  private readonly _cdr = inject(ChangeDetectorRef);

  // Reference to the dialog instance, used to close it and return the selection.
  private readonly _dialogRef = inject(DialogRef);

  /**
   * A map of candidates grouped by their navigation type.
   * The keys are navigation types, and the values are arrays of FeatureDetail objects.
   */
  candidates: ExtendedMap<FeatureDetail[]>;

  /**
   * The currently selected feature detail.
   * This is set when the user makes a selection.
   */
  selection!: FeatureDetail;

  /**
   * Initializes the dialog by grouping the injected `DIALOG_DATA` candidates by
   * navigation type. The dialog data and `DialogRef` are obtained via `inject()`.
   */
  constructor() {
    const data = inject<FeatureDetail[]>(DIALOG_DATA);

    // Initialize the candidates map and group the input data by navigation type.
    this.candidates = new ExtendedMap<FeatureDetail[]>();
    data.forEach((candidate: FeatureDetail) => {
      const navTypeArray = this.candidates.get(candidate.navType);
      if (!navTypeArray) {
        this.candidates.set(candidate.navType, [candidate]);
      } else {
        if (navTypeArray.indexOf(candidate) === -1) {
          this.candidates.set(candidate.navType, [...navTypeArray, candidate]);
        }
      }
    });

    // Log the grouped candidates for debugging purposes.
    this._logger.trace(
      `[DisambiguationDialogComponent] candidates:`,
      this.candidates
    );
  }

  /**
   * Handles the selection of a feature detail.
   * Closes the dialog and returns the selected item to the caller.
   * @param item - The selected FeatureDetail object.
   */
  select(item: FeatureDetail) {
    this._dialogRef.close(item);
    this._cdr.detectChanges();
  }
}
